import { Formik } from "formik";
import {
  TextArea,
  TextInput,
  CancelButton,
  SubmitButton,
  Form,
  isEmpty,
  ButtonGroup,
  TenantTypeahead,
  ProductCategoryTypeahead,
  CompanyTypeaheadWithTenant,
  SelectBox,
  useNotifier,
  BadRequestError,
  isNothing,
  url,
  useApi,
  useTranslateError,
} from "lcm-iot-commons";

import { useNavigate } from "react-router-dom-v5-compat";
import { useIntl } from "react-intl";
import ProductStatusSelect from "./ProductStatusSelect";
import ProductTypeahead from "./ProductTypeahead";

type OwnProps = {
  edit?: boolean;
  initialValues?: {
    id?: number;
    tenant?: {
      id?: number;
      name?: string;
    };
    manufacturer?: {
      id?: number;
      name?: string;
    };
    code?: string;
    name?: string;
    description?: string;
    status?: {
      id?: number;
      name?: string;
    };
    category?: {
      id?: number;
      name?: string;
    };
    riskOfMaintainability?: string;
    parent?: {
      id?: number;
      code?: string;
    };
  };
};

const defaultProps: Partial<OwnProps> = {
  edit: false,
  initialValues: {},
};

type Props = OwnProps & typeof defaultProps;

export default function ProductForm({ edit, initialValues }: Props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const notifier = useNotifier();
  const api = useApi();
  const translateError = useTranslateError();
  const validateForm = (values: $TSFixMe) => {
    const errors = {};
    if (!edit && !values.tenant) {
      (errors as $TSFixMe).tenant = intl.formatMessage({
        id: "validation.product.tenant.mandatory",
      });
    }
    if (!values.manufacturer) {
      (errors as $TSFixMe).manufacturer = intl.formatMessage({
        id: "validation.product.manufacturer.mandatory",
      });
    }
    if (isEmpty(values.code)) {
      (errors as $TSFixMe).code = intl.formatMessage({
        id: "validation.product.code.mandatory",
      });
    }
    if (isEmpty(values.name)) {
      (errors as $TSFixMe).name = intl.formatMessage({
        id: "validation.product.name.mandatory",
      });
    }
    if (!values.status) {
      (errors as $TSFixMe).status = intl.formatMessage({
        id: "validation.product.status.mandatory",
      });
    }
    return errors;
  };

  const risksOfMaintainability = [
    {
      id: "low",
      name: intl.formatMessage({ id: "enum.risk_of_maintainability.low" }),
    },
    {
      id: "medium",
      name: intl.formatMessage({ id: "enum.risk_of_maintainability.medium" }),
    },
    {
      id: "high",
      name: intl.formatMessage({ id: "enum.risk_of_maintainability.high" }),
    },
    {
      id: "undefined",
      name: intl.formatMessage({
        id: "enum.risk_of_maintainability.undefined",
      }),
    },
  ];
  const displayedValues = edit
    ? {
        ...initialValues,
        riskOfMaintainability: risksOfMaintainability.find(
          (r) => r.id === (initialValues as $TSFixMe)?.riskOfMaintainability,
        ),
      }
    : initialValues;

  const { mutateAsync: createTenant } = api.post.useMutation("/tenants", {
    onError: () => {},
  });
  const { mutateAsync: createManufacturer } = api.post.useMutation(
    "/companies",
    {
      onError: () => {},
    },
  );
  const { mutateAsync: createCategory } = api.post.useMutation(
    "/product/categories",
  );
  const { mutateAsync: handleProduct } = edit
    ? api.patch.useMutation(`/products/${initialValues?.id}`)
    : api.post.useMutation("/products", {
        onError: () => {},
      });

  const { mutateAsync: assignProductCategory } = edit
    ? api.patch.useMutation()
    : api.post.useMutation("/product/categories/:id/products");

  const { mutateAsync: unassignProductCategory } = api.delete.useMutation(
    `/products/${initialValues?.id}/categories`,
  );

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting, values } = formProps;
    return (
      <Form id="product-form" {...formProps}>
        <TenantTypeahead
          {...formProps}
          id="product-tenant"
          name="tenant"
          disabled={edit}
          label={intl.formatMessage({ id: "label.tenant" })}
          canCreate
          required={!edit}
        />
        <CompanyTypeaheadWithTenant
          {...formProps}
          id="product-manufacturer"
          name="manufacturer"
          label={intl.formatMessage({ id: "label.manufacturer" })}
          tenantId={values.tenant?.id}
          onlyCreate={values.tenant?.new || !values.tenant}
          canCreate
          required
        />
        <TextInput
          {...formProps}
          id="product-code"
          name="code"
          label={intl.formatMessage({ id: "label.product_code" })}
          required
        />
        <TextInput
          {...formProps}
          id="product-name"
          name="name"
          label={intl.formatMessage({ id: "label.name" })}
          required
        />
        <TextArea
          {...formProps}
          id="product-description"
          name="description"
          label={intl.formatMessage({ id: "label.description" })}
        />
        <ProductStatusSelect
          {...formProps}
          id="product-status"
          name="status"
          label={intl.formatMessage({ id: "label.status" })}
          required
        />
        <ProductTypeahead
          {...formProps}
          id="product-parent"
          name="parent"
          excludeProductId={values.id}
          tenantId={values.tenant?.id}
          manufacturerId={values.manufacturer?.id}
          label={intl.formatMessage({ id: "label.product_parent" })}
        />
        <SelectBox
          {...formProps}
          id="product-risk-of-maintainability"
          name="riskOfMaintainability"
          label={intl.formatMessage({ id: "label.risk_of_maintainability" })}
          options={risksOfMaintainability}
        />
        <ProductCategoryTypeahead
          {...formProps}
          id="product-category-typeahead"
          name="category"
          tenantId={values.tenant?.id}
          label={intl.formatMessage({ id: "label.category" })}
          canCreate
        />
        <ButtonGroup>
          <SubmitButton
            id="product-form-submit-button"
            fetching={isSubmitting}
            disabled={!formProps.dirty || isSubmitting}
          />
          <CancelButton
            id="product-form-cancel-button"
            fetching={isSubmitting}
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; fetching: any; intl: IntlShape... Remove this comment to see the full error message
            intl={intl}
          />
        </ButtonGroup>
      </Form>
    );
  };

  const handleOnSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      const { code, name, description, status, parent, riskOfMaintainability } =
        values;
      let { tenant, manufacturer, category } = values;

      const onError = (error: $TSFixMe, key: string, value?: string) => {
        if (error instanceof BadRequestError && error.contains("taken")) {
          const errorKey = value || key;
          actions.setErrors({
            [key]: intl.formatMessage({
              id: `api.error.${errorKey}.taken`,
            }),
          });
        } else {
          notifier.showError(translateError(error));
          navigate(url("/products"));
        }
      };

      if (tenant.new) {
        const onCreateTenantSuccess = (data: $TSFixMe) => {
          tenant = { id: data.id };
        };

        const tenantPayload = { name: tenant.name };

        await createTenant(tenantPayload, {
          onSuccess: onCreateTenantSuccess,
          onError: (error) => onError(error, "tenant"),
        });
      }

      if (manufacturer.new) {
        const onCreateManufacturerSuccess = (data: $TSFixMe) => {
          manufacturer = { id: data.id };
        };

        const manufacturerPayload = {
          name: manufacturer.name,
          tenant: { id: tenant.id },
        };

        await createManufacturer(manufacturerPayload, {
          onSuccess: onCreateManufacturerSuccess,
          onError: (error) => onError(error, "manufacturer"),
        });
      }

      if (category?.new) {
        const onCreateCategorySuccess = (data: $TSFixMe) => {
          category = { id: data.id };
        };

        const categoryPayload = {
          name: category.name,
          tenant: { id: tenant.id },
        };

        await createCategory(categoryPayload, {
          onSuccess: onCreateCategorySuccess,
        });
      }
      const productPayload = {
        product_code: code,
        manufacturer: { id: manufacturer.id },
        status: { id: status.id },
        tenant: { id: tenant.id },
        risk_of_maintainability: isNothing(riskOfMaintainability)
          ? "undefined"
          : riskOfMaintainability.id,
        name,
        description: description || "",
        parent: parent ? { id: parent.id } : null,
      };
      let product: { id: number } = { id: 0 };

      const onHandleProductSuccess = (data: $TSFixMe) => {
        product = edit ? { id: initialValues?.id } : { id: data.id };

        if (edit) {
          notifier.showSuccess(
            intl.formatMessage({
              id: "product_update.success_notification",
            }),
          );
        } else {
          notifier.showSuccess(
            intl.formatMessage({
              id: "product_create.success_notification",
            }),
          );
        }

        navigate(url(`/products/${product.id}`));
      };

      await handleProduct(productPayload, {
        onSuccess: onHandleProductSuccess,
        onError: (error) => onError(error, "code", "product_code"),
      });

      if (category) {
        const mutationUrl = edit
          ? `/products/${initialValues?.id}/categories`
          : `/product/categories/${category.id}/products`;
        const payload = edit
          ? { mutationBody: { categories: [{ id: category.id }] }, mutationUrl }
          : { mutationBody: { products: [{ id: product?.id }] }, mutationUrl };

        await assignProductCategory(payload);
      } else if (initialValues?.category?.id) {
        const categoryPayload = {
          categories: [{ id: initialValues.category.id }],
        };

        await unassignProductCategory(categoryPayload);
      }
    } catch {
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik
      id="product-form"
      initialValues={displayedValues}
      onSubmit={handleOnSubmit}
      validate={validateForm}
    >
      {renderForm}
    </Formik>
  );
}

ProductForm.defaultProps = defaultProps;
